import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { approvalService } from '@/services/approval.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';

/**
 * Brand-level release verdict of the viewer for the CURRENT effective team (God Mode: currentTeamId).
 * Used by the composers in CREATE mode, where no post id exists yet to ask `release-permissions`.
 *
 * Fail closed: while loading, on any error (incl. 404 from an older server) or when the server says the
 * workflow is not active, both flags are false, so no release / send option is ever shown optimistically.
 */
export interface ReleaseCapabilities {
  isLoading: boolean;
  canSendForReview: boolean;
  canReleaseDirect: boolean;
}

export const useReleaseCapabilities = (enabled = true): ReleaseCapabilities => {
  const { teamId, hasTeam } = useEffectiveTeamId();
  // Only brands with an active release workflow can have these options
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  const queryEnabled = enabled && isAgencyTeam && !carteBlanche && hasTeam && teamId !== null;

  const query = useQuery({
    queryKey: teamQueryKeys.approval.releaseCapabilities(teamId),
    networkMode: 'always',
    queryFn: () => approvalService.getReleaseCapabilities(teamId as number),
    enabled: queryEnabled,
    // A failed verdict simply hides the options; no retry storm
    retry: false,
    // Depends on roles and the carte blanche switch, which other people change
    staleTime: 0,
    refetchOnMount: 'always',
  });

  return useMemo(() => {
    const data = query.data;
    const ok = queryEnabled && !query.isError && !!data && data.workflowActive === true;
    return {
      // A disabled query is pending forever: only an enabled one can be "loading"
      isLoading: queryEnabled && query.isPending,
      canSendForReview: ok && data.canSendForReview === true,
      canReleaseDirect: ok && data.canReleaseDirect === true,
    };
  }, [query.data, query.isError, query.isPending, queryEnabled]);
};
